// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import {
  Form,
  Input,
  message,
  Select,
  Checkbox,
  InputNumber,
  Modal,
} from "antd";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
  sortData,
} from "../../utils/Constants";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import {
  addUserThunk,
  deleteUserThunk,
  getUserListThunk,
  getRoleListThunk,
  editUserThunk,
  getDiemListThunk,
  getDesignationListThunk,
  getExpenseCatsListThunk,
} from "../../store/thunks/usersThunk";
import EditForm from "../../components/molecules/Forms/editForm";
import media from "../../media";
import { selectedRowInfo } from "../../store/slices/authSlice";
import {
  getDepartmentListThunk,
  getProjectCodeListThunk,
} from "../../store/thunks/masterDataThunk";
import { projectCodesSlice } from "../../store/slices/projectCodesSlice";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  roles,
  departments,
  ...restProps
}) => {
  let inputNode;
  if (dataIndex === "role") {
    inputNode = (
      <Select
        isclearable="true"
        options={roles}
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else if (dataIndex === "department") {
    inputNode = (
      <Select
        isclearable="true"
        options={departments}
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else {
    inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  }
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
const Users = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeToken = useSelector((state) => state.auth.accessToken);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const users = useSelector((state) => state.users.list) || [];
  const usersLoading = useSelector((state) => state.users.loading);
  const storePageInfo = useSelector((state) => state.users.pageInfo);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [storeTotalCount, setStoreTotalCount] = useState(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [canSendExpenseRequest, setCanSendExpenseRequest] = useState();
  const [canDelegateAccess, setCanDelegateAccess] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]),
    [roles, setRoles] = useState([]),
    [managers, setManagers] = useState([]),
    [departments, setDepartments] = useState([]),
    [designations, setDesignations] = useState([]),
    [selectedRole, setSelectedRole] = useState();
  const [profilePicInfo, setProfilePicInfo] = useState("");
  const [profilePicWithUrlChecked, setProfilePicWithUrlChecked] =
    useState(false);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const [projectCodes, setProjectCodes] = useState([]);
  const [expenseCats, setExpenseCats] = useState([]);
  const [diems, setDiems] = useState([]);

  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch users, roles, departments
  const fetchUsers = async () => {
    const res = await dispatch(
      getUserListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      }),
    ).unwrap();

    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------

  useEffect(() => {
    const requests = [
      dispatch(getRoleListThunk()).unwrap(),
      dispatch(getDepartmentListThunk()).unwrap(),
      dispatch(getDesignationListThunk()).unwrap(),
      dispatch(getProjectCodeListThunk()).unwrap(),
      dispatch(getDiemListThunk()).unwrap(),
      dispatch(getExpenseCatsListThunk()).unwrap(),
    ];

    Promise.all(requests)
      .then(
        ([
          roles,
          departments,
          designations,
          projectCodes,
          diems,
          expenseCats,
        ]) => {
          setRoles(roles || []);
          setDepartments(departments || []);
          setDesignations(designations || []);
          setProjectCodes(projectCodes || []);
          setExpenseCats(expenseCats || []);
          setDiems(diems || []);
        },
      )
      .catch((err) => {
        console.error("Master data fetch error:", err);
      })
      .finally(() => setShowLoader(false));
  }, [currentPath, dispatch, storeRole]);

  useEffect(() => {
    setFilteredUsers(users); // sync on data change
  }, [users, projectCodes]);
  useEffect(() => {
    fetchUsers();
  }, [currentPath, dispatch]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingUser(record); // store the record to edit
    tableForm.setFieldsValue({
      role: record?.role,
      empID: record?.empID,
      name: record?.name,
      email: record?.email,
      //department: record?.department,
      designation: record?.designation,
      location: record?.location,
      diemId: record?.diemId,
      projectCodeID: record?.projectCodeID,
      expenseCatID: record?.expenseCatID,
      canDelegateAccess: record.canDelegateAccess,
      canSendExpenseRequest: record.canSendExpenseRequest,
      status: record?.status,
    });
    setIsEditModalOpen(true); // open the modal
  };

  const handleCheckboxChange = (e) => {
    setProfilePicWithUrlChecked(e.target.checked);
  };

  const inputComponent = <Input className="w-100 p-2 rounded-2" />;

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    // Read image parameters
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        // Check for SVG file type
        const isSVG = file.type === "image/svg+xml";
        img.src && setProfilePicInfo(img.src);
      };
    };

    reader.readAsDataURL(file);
  };

  const getBase64ImageExtension = (urlOrBase64) => {
    // Check if the input is a Base64 string
    if (urlOrBase64.startsWith("data:image")) {
      // Extract the MIME type from the Base64 string
      const mimeTypeMatch = urlOrBase64.match(
        /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/,
      );
      if (mimeTypeMatch && mimeTypeMatch[1]) {
        // Extract the file extension from the MIME type
        const extension = mimeTypeMatch[1].split("/")[1];
        return extension;
      } else {
        // Default to empty string if no extension found
        return "";
      }
    } else {
      // Extract the file extension from the URL
      const ext = urlOrBase64.split(".").pop().split("?")[0];
      return ext;
    }
  };

  const headers = {
    "Content-Type": "multipart/form-data", // Make sure to set the content type
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
      className: "all-nowrap-column",
    },
    {
      title: "Emp. ID",
      dataIndex: "empID",
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Name",
      dataIndex: "name",
      editable: true,
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Role",
      dataIndex: "role",
      editable: true,
      render: (text) => <>{text}</>,
    },
    {
      title: "Email",
      dataIndex: "email",
      editable: true,
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Department",
      dataIndex: "department",
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Designation",
      dataIndex: "designation",
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Location",
      dataIndex: "location",
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Band",
      dataIndex: "diemId",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        const diemInfo = diems.find((diemItem) => diemItem.value == text);

        if (typeof diemInfo?.label !== "string") return null;

        return <>{diemInfo?.label}</>;
      },
    },
    {
      title: "Project Code",
      dataIndex: "projectCodeID",
      className: "all-nowrap-column",
      render: (text, record, index) => {
        const projectCodeInfo = projectCodes.find(
          (projectCodeItem) => projectCodeItem.value == text,
        );

        if (typeof projectCodeInfo?.label !== "string") return null;
        const [code = "", companyName = ""] =
          (projectCodeInfo?.label && projectCodeInfo?.label?.split(" - ")) ||
          [];

        return (
          <>
            <div>{code}</div>
            <small>{companyName}</small>
          </>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => (
        <>
          <span className={`${text?.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      className: "all-nowrap-column",
      render: (text) => text && formateDateTime(text),
    },
    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            className: "all-nowrap-column",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",

            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        roles: roles,
        departments: departments,
        editing: isEditing(record),
      }),
    };
  });

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        }),
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Role",
      name: "role",
      rules: [
        {
          required: true,
          message: "Please select role",
        },
      ],
      placeholder: "Select Role",
      type: "select",
      options: roles,
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      onChange: (val, data) => {
        setSelectedRole(data?.label);
      },
    },
    {
      isVisible: true,
      label: "Employee ID",
      name: "empID",
      rules: [
        {
          required: true,
          message: "Please input employee id",
        },
      ],
      placeholder: "Enter the employee id",
      type: "input",
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
    },
    {
      isVisible: true,
      label: "Name",
      name: "name",
      rules: [
        {
          required: true,
          message: "Please input name",
        },
      ],
      placeholder: "Enter the name",
      type: "input",
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
    },
    {
      isVisible: true,
      label: "Email",
      name: "email",
      rules: [
        { required: true, message: "Please input your email!" },
        {
          type: "email",
          message: "Please enter a valid email address",
        },
      ],
      placeholder: "Enter the email",
      type: "input",
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
    },
    {
      isVisible: true,
      label: "Department",
      name: "department",
      rules: [
        {
          required: true,
          message: "Please select department",
        },
      ],
      placeholder: "Select Department",
      type: "select",
      //multiple: true,
      options: departments,
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Designation",
      name: "designation",
      rules: [
        {
          required: true,
          message: "Please select designation",
        },
      ],
      placeholder: "Select Designation",
      type: "select",
      options: designations,
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Location",
      name: "location",
      rules: [
        {
          required: true,
          message: "Please select location",
        },
      ],
      placeholder: "Select an option",
      type: "select",
      options: [
        { label: "Mohali", value: "Mohali" },
        { label: "Pune", value: "Pune" },
      ],
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
    },
    {
      isVisible: true,
      label: "Band",
      name: "diemId",
      rules: [
        {
          required: true,
          message: "Please select band",
        },
      ],
      placeholder: "Select Band",
      type: "select",
      options: diems,
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Project Code",
      name: "projectCodeID",
      rules: [
        {
          required: true,
          message: "Please select project code",
        },
      ],
      placeholder: "Select Project Code",
      type: "select",
      options: projectCodes,
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Expense Category",
      name: "expenseCatID",
      rules: [
        {
          required: true,
          message: "Please select expense category",
        },
      ],
      placeholder: "Select an option",
      type: "select",
      options: [
        { label: "OPEX", value: "OPEX" },
        { label: "CAPEX", value: "CAPEX" },
      ],
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
    },
    {
      isVisible: true,
      label: "Allow this user to delegate access",
      name: "canDelegateAccess",
      rules: [
        {
          required: true,
          message: "Please select an option",
        },
      ],
      placeholder: "Select an option",
      type: "select",
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      onChange: (val) => {
        setCanDelegateAccess(val);
      },
    },
    {
      isVisible: true,
      label: "Can send an Expense Request?",
      name: "canSendExpenseRequest",
      rules: [
        {
          required: true,
          message: "Please select an option",
        },
      ],
      placeholder: "Select an option",
      type: "select",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      colProps: {
        xs: 24,
        md: 12,
        lg: 12,
      },
      onChange: (val) => {
        setCanSendExpenseRequest(val);
      },
    },
    {
      isVisible: true,
      type: "custom",
      render: () => (
        <>
          <Form.Item label="Profile Image" name="profilePicUrl">
            {profilePicWithUrlChecked ? (
              inputComponent
            ) : (
              <input
                type="file"
                onChange={handleFileChange}
                className="upload-list-block"
              />
            )}
          </Form.Item>
          <Form.Item label="" name="profilePicUrl">
            <Checkbox
              checked={profilePicWithUrlChecked}
              onChange={handleCheckboxChange}
              className="form-label-antd profilePicWithUrlCheckBox"
              name="profilePicWithUrlCheckBox"
            >
              Upload with url
            </Checkbox>
          </Form.Item>
        </>
      ),
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      <div className="bgi-no-repeat card-stretch rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Users"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name="Add New User"
          searchClassName="table-search"
          searchPlaceholder="Search User's Role, Emp. ID, Name, Email, Department and Status"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={users}
          onFilteredData={setFilteredUsers}
          searchKeys={[
            "role",
            "empID",
            "name",
            "email",
            "designation",
            "department",
            "location",
            "diemId",
            "status",
          ]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            route: currentPath,
            roleID: values?.role,
            empID: parseInt(values?.empID),
            name: values?.name,
            email: values?.email,
            departmentID: values?.department,
            designation: values.designation,
            location: values?.location,
            diemId: values?.diemId,
            projectCodeID: values?.projectCodeID,
            imageName:
              parseInt(values?.empID) +
              "_" +
              values?.name?.replaceAll(" ", "_"),
            uploadType:
              profilePicWithUrlChecked === true ? "thumbUrl" : "other",
            profilePicUrl:
              profilePicWithUrlChecked === true
                ? values?.profilePicUrl
                : profilePicInfo
                  ? profilePicInfo
                  : null,
            ext:
              profilePicWithUrlChecked === true
                ? getBase64ImageExtension(values?.profilePicUrl)
                : profilePicInfo
                  ? getBase64ImageExtension(profilePicInfo)
                  : "", //values?.profilePicUrl?.split(".").pop()
            //createdAt: new Date().toISOString(),
            canSendExpenseRequest: values?.canSendExpenseRequest,
            canDelegateAccess: values?.canDelegateAccess,
            expenseCatID: values?.expenseCatID,
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
          })}
          list={addUserThunk}
          formFields={formFields}
          headers={headers}
          successMessage="User added successfully."
          onSuccess={fetchUsers}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteUserThunk,
              route: "users",
              actionName: "user",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />

        <div className="card p-5 rounded-lg paddingTop-0">
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={rowSelection}
                    storeList={filteredUsers}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getUserListThunk({
                              route: currentPath,
                              pagination,
                            }),
                          ),
                        page,
                        pageSize,
                        storeTotalCount,
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit User"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Role",
              name: "role",
              type: "select",
              options: roles, // pass your roles array [{ label, value }]
              rules: [{ required: true, message: "Please select user role!" }],
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              filterOption: (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase()),
            },
            {
              label: "Employee ID",
              name: "empID",
              type: "input",
              props: { disabled: true },
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
            },
            {
              label: "Name",
              name: "name",
              type: "input",
              rules: [{ required: true, message: "Please input user name!" }],
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
            },
            {
              label: "Email",
              name: "email",
              type: "input",
              props: { type: "email" },
              rules: [{ required: true, message: "Please input user email!" }],
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
            },
            {
              label: "Department",
              name: "department",
              rules: [
                {
                  required: true,
                  message: "Please select department",
                },
              ],
              placeholder: "Select Department",
              type: "select",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              //multiple: true,
              options: departments,
              filterOption: (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase()),
            },
            {
              label: "Designation",
              name: "designation",
              type: "select",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              options: designations,
              rules: [{ required: true, message: "Please select designation" }],
              placeholder: "Select Designation",
              filterOption: (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase()),
            },
            {
              label: "Location",
              name: "location",
              type: "select",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              options: [
                { label: "Mohali", value: "Mohali" },
                { label: "Pune", value: "Pune" },
              ],
              rules: [{ required: true, message: "Please select location" }],
              placeholder: "Select Location",
              filterOption: (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase()),
            },
            {
              label: "Band",
              name: "diemId",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              rules: [
                {
                  required: true,
                  message: "Please select band",
                },
              ],
              placeholder: "Select Band",
              type: "select",
              options: diems,
              filterOption: (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase()),
            },
            {
              label: "Project Code",
              name: "projectCodeID",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              rules: [
                {
                  required: true,
                  message: "Please select project code",
                },
              ],
              placeholder: "Select Project Code",
              type: "select",
              options: projectCodes,
              filterOption: (input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase()),
            },
            {
              label: "Expense Category",
              name: "expenseCatID",
              rules: [
                {
                  required: true,
                  message: "Please select expense category",
                },
              ],
              placeholder: "Select an option",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              type: "select",
              options: expenseCats,
            },
            {
              label: "Allow this user to delegate access",
              name: "canDelegateAccess",
              type: "select",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
              rules: [{ required: true, message: "Please select an option!" }],
            },
            {
              label: "Can send an Expense Request?",
              name: "canSendExpenseRequest",
              type: "select",
              colProps: {
                xs: 24,
                md: 12,
                lg: 12,
              },
              rules: [
                {
                  required: true,
                  message: "Please select an option",
                },
              ],
              placeholder: "Select an option",
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
            },
            {
              label: "Status",
              name: "status",
              type: "select",
              colProps: {
                xs: 24,
                md: 24,
                lg: 24,
              },
              options: [
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
                { label: "Softdelete", value: "SOFTDELETE" },
              ],
              rules: [{ required: true, message: "Please select status!" }],
            },
          ]}
          submitThunk={editUserThunk}
          payloadBuilder={(values) => {
            const payload = {
              route: currentPath,
              id: editingUser.key,
              empID: parseInt(values?.empID),
              name: values?.name,
              email: values?.email,
              designation: values.designation,
              location: values?.location,
              canDelegateAccess: values.canDelegateAccess,
              canSendExpenseRequest: values.canSendExpenseRequest,
              status: values.status,
              historySource: "backend",
              historyAction: "update",
              historyUserID: storeLIUId,
              roleID: values.role,
              //departmentID : values.department,
              diemId: values.diemId,
              projectCodeID: values.projectCodeID,
              expenseCatID: values.expenseCatID,
            };
            if (values.role && values.role !== editingUser?.role) {
              payload.roleID = values.role;
            }
            // if (values.department && values.department !== editingUser?.department) {
            //   payload.departmentID = values.department;
            // }
            if (values.diemId && values.diemId !== editingUser?.diemId) {
              payload.diemId = values.diemId;
            }
            if (
              values.projectCodeID &&
              values.projectCodeID !== editingUser?.projectCodeID
            ) {
              payload.projectCodeID = values.projectCodeID;
            }
            if (
              values.expenseCatID &&
              values.expenseCatID !== editingUser?.expenseCatID
            ) {
              payload.expenseCatID = values.expenseCatID;
            }
            return payload;
          }}
          successMessage="User updated successfully."
          currentPath={currentPath}
          refreshListThunk={getUserListThunk}
        />
      </div>
    </div>
  );
};

export default Users;
